import React, { useState, useEffect, useCallback } from "react";
import { graphql } from "gatsby"
import Flink from '~/components/shared/flink'
import { useTheme } from '~/contexts/ThemeContext';
import useVintInventoryHook from '~/utils/vint-inventory'
import useAnalytics from '~/utils/analytics'
import { imgSize, notBlank } from '~/utils/helpers'

import { Seo } from '~/components/shared/seo'
import KlaviyoNews from '~/components/shared/klaviyo-news'


import * as styles from "~/styles/plp/vintage_plp.module.scss"


const tile_image = (media) => {
  const image = media.find( x => x.type === 'IMAGE')
  return image ? image : false
}

const proc_date = (date) => {
  const ddate = new Date( parseInt(date) )
  const format_date = ddate.toLocaleDateString('en-us', { year: 'numeric', month: 'short', day: 'numeric' })
  const format_time = ddate.toLocaleTimeString('en-US', {hour: '2-digit', minute:'2-digit'})

  return {date: format_date, time: format_time}
}


export default function CollectionTemplate({ data: { vintCollect } }) {
  const collection = vintCollect
  const { setBlockHeader, bread, setBread, analytics, 
          setAnalytics } = useTheme()
  const { get_collinv } = useVintInventoryHook()
  const { ana_plp } = useAnalytics()

  const { handle, drop, drop_month, date, pasts, title } = collection
  
  const [products, setProducts] = useState(collection.products)
  const [plpInv, setPlpInv] = useState(false)

  const handleScroll = (e) => {
    if (!e.target.closest('a')) return
    setBread(curr => {curr.scroll = window.scrollY; return curr})
  }
  
  const checkBread = () => {
    if (bread && bread.click) {
      const newtop = bread.scroll
      setBread(curr => {curr.click = false; return curr})
    }
    if (!bread || bread.handle !== handle) {
      setBread({handle: handle, title: title, scroll: 0, click: false})
    }
  }

  const check_date = () => {
    return parseInt(date) > Date.now()
  }

  const fprice = (fpp, ffp) => {
    return ffp > fpp ? ffp : fpp
  }

  const checkInv = useCallback( async () => { 
    const inv = await get_collinv(collection.products)
    setPlpInv(inv)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  const parseProds = useCallback((plpInv) => { 
    if (check_date() || !plpInv) return

    const new_prods = collection.products.map(prod => {
      const inv = plpInv.find(x => x.id === prod.shop_id)
      if (inv) {
        prod.available = inv.available
        prod.first_full_price = inv.first_full_price
        prod.first_product_price = inv.first_product_price
      }
      return prod
    })
    setProducts(new_prods)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setBread({handle: handle, title: title})
  }, [setBread, handle, title])

  useEffect(() => {
    checkInv()
  }, [checkInv]);

  useEffect(() => {
    if (!plpInv) return

    parseProds(plpInv)
  }, [plpInv, parseProds])

  useEffect(() => {
    setAnalytics({...analytics, list: `/vintage/${handle}`})

    checkBread()
    setBlockHeader(false)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!products || products.length < 1) return

    ana_plp(products, handle, true)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products])


  useEffect(() => {
    window.addEventListener('click', handleScroll);

    return () => {
      window.removeEventListener('click', handleScroll);
    };
  })



  return (
    <div className={styles.vinplp}>

      <VintEmail />
      
      <div className={styles.vinplp_head}>
        <h1 className={styles.vinplp_title}>
          {handle === 'vintage-collection' ? (
            <>
              Vintage Collection
            </>
          ) : (
            <>
              Vintage Collection {drop}
            </>
          )}
        </h1>
        <div className={styles.vinplp_count}>
          {products && products.length} Items
        </div>
      </div>

      {pasts.length > 0 && (
        <div className={styles.vint_bar}>
          {pasts && pasts.map( (pdrop, index) =>
            <Flink key={`pdrop${index}`} href={`/vintage/${pdrop.handle}`} className={handle === pdrop.handle ? styles.curr : ''}>{pdrop.drop}</Flink>
          )}
        </div>
      )}

      {check_date() ? (
        <ComingSoon collection={collection} />
      ) : (
        <div className={styles.vtiles}>
          {products && products.map( ( product, index ) => {

            const price = fprice(product.first_product_price, product.first_full_price)
            const sold = price === 0 || !product.available

            return (
              <div key={product.handle} className={styles.vtile}>
                <div className={styles.vtile_drop}>
                  {drop_month}{drop} - {index < 9 ? `0${index + 1}` : index + 1}
                </div>
                <Flink href={`/products/${product.handle}`} className={styles.vtile_image}>
                  {!!tile_image(product.media) && (
                    <img 
                      src={imgSize(tile_image(product.media).src,800)} 
                      alt={index === 0 ? tile_image(product.media).alt.replace('Pictures of  ', `Pictures of ${product.title} `) : ''}
                      className={styles.vtile_img} 
                    />
                  )}
                </Flink>
                <div className={styles.vtile_infos}>
                  <div className={styles.vtile_info}>
                    <Flink href={`/products/${product.handle}`} className={styles.vtile_title}>
                      {product.title}
                    </Flink>
                    <div className={styles.vtile_price}>
                      {sold ? (
                        <>
                          SOLD
                        </>
                      ) : (
                        <>
                          ${price}
                        </>
                      )}
                    </div>
                  </div>
                  {notBlank(product.year) && (
                    <div className={styles.vtile_info}>
                      <div className={styles.vtile_label}>
                        Year
                      </div>
                      <div className={styles.vtile_data}>
                        {product.year}
                      </div>
                    </div>
                  )}
                  {(product.hasOwnProperty('size') && notBlank(product.size)) && (
                    <div className={styles.vtile_info}>
                      <div className={styles.vtile_label}>
                        Size
                      </div>
                      <div className={styles.vtile_data}>
                        {product.size}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const VintEmail = () => {
  return (
    <div className={styles.vemail_wr}>
      <div className={styles.vemail}>
        <div className={styles.title}>
          Sign up to be notified about our next archival batch
        </div>
        <div className={styles.vemail_form}>
          <KlaviyoNews listId={'VSvZQQ'} formId={'TyaQ82'} bg={'#f8f6f5'} place={'Enter your email'} />
        </div>
      </div>
    </div>
  )
}


const ComingSoon = ({ collection }) => {
  const { isMobile } = useTheme()

  const { drop_month, drop, date } = collection
  const limit_prods = collection.products.slice(0,4)

  const fdate = proc_date(date)

  return (
    <div className={styles.vsoon}>
      {limit_prods.length < 1 && (
        <img 
          src={imgSize('https://cdn.shopify.com/s/files/1/0123/5065/2473/files/Vintage_ComingSoon_DK2_2.jpg?v=1688680214',!isMobile ? 1300 : 900)}
          className={styles.vsoon_img} 
          alt="" 
        />
      )}
      <div className={styles.title}>
        Coming Soon - Vintage Collection {drop}
        {notBlank(date) && (
          <p>
            {fdate.date}&nbsp;{fdate.time}
          </p>
        )}
      </div>

      <div className={styles.vsoon_copy}>
        Our signature styles draw from wardrobe staples of the past that are icons in their own right. Timeless 
        utilitarian pieces that represent the foundation of American clothing and culture today. Cherished for 
        decades, these vintage pieces are as stylish today as when they first debuted. Check back shortly for 
        our next drop of carefully selected vintage pieces.
      </div>

      {limit_prods.length > 0 && (
        <div className={styles.vtiles}>
          {limit_prods.map( ( product, index ) => {

            const img = tile_image(product.media)

            return (
              <div key={product.handle} className={styles.vtile}>
                <div className={styles.vtile_drop}>
                  {drop_month}{drop} - {index < 9 ? `0${index + 1}` : index + 1}
                </div>
                <div className={styles.vsoon_image}>
                  {!!img && (
                    <img 
                      src={imgSize(tile_image(product.media).src,800)} 
                      alt={img.alt.toLowerCase().includes('product flat') ? `${product.title} - ${img.alt}` : img.alt}
                      className={styles.vsoon_img} 
                    />
                  )}
                </div>
                <div className={styles.vtile_infos}>
                  <div className={styles.vtile_info}>
                    <div className={styles.vtile_title}>
                      {product.title}
                    </div>
                    <div className={styles.vtile_price}>
                      COMING SOON
                    </div>
                  </div>
                  {notBlank(product.year) && (
                    <div className={styles.vtile_info}>
                      <div className={styles.vtile_label}>
                        Year
                      </div>
                      <div className={styles.vtile_data}>
                        {product.year}
                      </div>
                    </div>
                  )}
                  {(product.hasOwnProperty('size') && notBlank(product.size)) && (
                    <div className={styles.vtile_info}>
                      <div className={styles.vtile_label}>
                        Size
                      </div>
                      <div className={styles.vtile_data}>
                        {product.size}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}


export const Head = ({ data: { vintCollect } }) => (
  <Seo 
    title={vintCollect.title}
    description={vintCollect.seo_desc ? vintCollect.seo_desc.replaceAll('\n','') : `The Buck Mason ${vintCollect.title} collection`}
    image={vintCollect.img}
    url={`/vintage/${vintCollect.handle}`}
  />
)





export const pageQuery = graphql`
  query ($handle: String!) {
    vintCollect(handle: { eq: $handle }) {
      handle
      title
      img
      drop
      drop_month
      date
      pasts
      description
      seo_desc
      
      products {
        available
        first_full_price
        first_product_price
        handle
        media
        mid
        pl_pima
        pub
        shop_id
        sku
        tags
        title
        variants

        is_vintage
        vcoll_title
        vcoll_handle
        size
        year
        vdescription
        condition
        details
      }

    }
  }
`;

// is_vintage


